import React, { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { useSelector } from 'react-redux';

import Header from 'components/Header';
import Field from 'components/Field';
import EventPanel from 'components/EventPanel';
import Notes from 'components/Notes';
import PlayerList from 'components/PlayerList';
import Stats from 'components/Stats';

import Loading from 'components/Loading';
import Error from 'components/Error';

import ModalsContainer from 'components/ModalsContainer';
import NetworkWatcher from 'components/NetworkWatcher';
import HotKeys from 'components/HotKeys';

import StyledApp from './StyledApp';

import en from 'languages/en.json';
import no from 'languages/no.json';

import {
  selectLang,
  selectNotAuthenticated,
  selectNotAuthenticatedCode,
  setNotAuthenticated,
} from 'features/config/configSlice';
import { setUserId } from 'features/user/userSlice';
import { selectError, selectLoading } from 'features/match/matchSlice';
import { getMatchById } from 'features/match/thunks';
import { selectShowPlayerList } from 'features/input/inputSlice';
import NotAuthenticated from './components/NotAuthenticated';
import getUserAccessById from './features/user/getUserAccessById';
import ErrorCode from './enums/errorCode';
import getPing from './features/ping/getPing';
import { networkActions } from './features/network/networkSlice';
import { useAppDispatch } from './hooks/hooks';
import RequestLog from './components/RequestLog';
import ls from 'localstorage-slim';

const messages = {
  en,
  no,
};

const { REACT_APP_SESSION_OK_TOKEN, REACT_APP_NO_PING } = process.env;

const App: React.FC = () => {
  const lang = useSelector(selectLang);
  const loading = useSelector(selectLoading);
  const notAuthenticated = useSelector(selectNotAuthenticated);
  const notAuthenticatedCode = useSelector(selectNotAuthenticatedCode);
  const error = useSelector(selectError);
  const dispatch = useAppDispatch();

  const hasClearedLog = ls.get('hasClearedLog') as boolean;
  if (!hasClearedLog) {
    ls.clear();
    ls.set('hasClearedLog', true, { ttl: 60 * 60 * 24 * 365 });
    //@TODO: This is deployed january 2024 and is for cleaning up the old usage of localStorage. This code can be changed to just `ls.flush()` later in 2024 when all users should have ran the inital cleanup. No need for the hasClearedLog check then as it will only remove expired items
  } else {
    ls.flush();
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const matchId = urlParams.get('matchId');
    const sessionOkToken = urlParams.get('sessionOkToken');
    const userId = urlParams.get('userId');
    const liveFeedId = urlParams.get('liveFeedId');

    dispatch(setUserId(userId));

    if (sessionOkToken !== REACT_APP_SESSION_OK_TOKEN) {
      dispatch(setNotAuthenticated(ErrorCode.WRONG_SESSION_OK_TOKEN));
    } else if (userId && liveFeedId) {
      getUserAccessById(userId, liveFeedId).then((response) => {
        if (response === 'Ok' && matchId) {
          dispatch(getMatchById(matchId));
        } else {
          dispatch(setNotAuthenticated(ErrorCode.NO_ACCESS_TO_FEED));
        }

        if (REACT_APP_NO_PING !== 'true') {
          setInterval(() => {
            getPing().then((response) => {
              dispatch(networkActions.setOffsetSeconds(response.data.offsetSeconds));
              dispatch(networkActions.setResponseTime(response.responseTime));
            });
          }, 10000);
        }
      });
    } else {
      dispatch(setNotAuthenticated(ErrorCode.MISSING_PARAMS));
    }
  }, [dispatch]);

  const showPlayerList = useSelector(selectShowPlayerList);

  if (notAuthenticated) {
    return (
      <IntlProvider locale={lang} messages={(messages as any)[lang]}>
        <NotAuthenticated errorCode={notAuthenticatedCode} />
      </IntlProvider>
    );
  }

  if (loading) {
    return (
      <IntlProvider locale={lang} messages={(messages as any)[lang]}>
        <Loading />
      </IntlProvider>
    );
  }

  if (error) {
    return (
      <IntlProvider locale={lang} messages={(messages as any)[lang]}>
        <Error />
      </IntlProvider>
    );
  }

  return (
    <IntlProvider locale={lang} messages={(messages as any)[lang]}>
      <div>
        <Header />
        <StyledApp>
          {showPlayerList ? <PlayerList /> : <Field />}
          <EventPanel />
          <div className="cards">
            <Stats />
            <Notes />
            <RequestLog />
          </div>
        </StyledApp>
        <ModalsContainer />
        <NetworkWatcher />
        <HotKeys />
      </div>
    </IntlProvider>
  );
};

export default App;
